import React, { useState, useCallback } from "react";
import dayjs from "dayjs";
import ja from "dayjs/locale/ja";
import {
  Eventcalendar,
  setOptions,
  localeJa,
  MbscEventcalendarView,
  MbscCalendarColor,
  CalendarNav,
  CalendarPrev,
  CalendarToday,
  CalendarNext,
  SegmentedGroup,
  SegmentedItem,
} from "@mobiscroll/react";
import useFetchResourcePlansByResource from "../../hooks/useFetchResourcePlansByResource";
import LoadingOverlay from "react-loading-overlay-ts";
import useRunningTimes from "../../hooks/useRunningTimes";
import getDayText from "../../hooks/useDayText";

dayjs.locale(ja);

setOptions({
  locale: localeJa,
  theme: "ios",
  themeVariant: "light",
});

type Props = {
  customerId: string;
  resourceId: string;
  basePath: string;
};

const Timeline: React.FC<Props> = ({ basePath, customerId, resourceId }) => {
  const [view, setView] = useState("month");
  const [calView, setCalView] = useState<MbscEventcalendarView>({
    timeline: { type: "month", eventList: true },
  });
  const [from, setFrom] = useState(() => dayjs().startOf("month").unix());
  const [to, setTo] = useState(() => dayjs().add(1, "month").startOf("month").unix());
  const { data: runningTimes } = useRunningTimes(basePath, customerId, resourceId, { from, to });
  const { plans, isLoading, isFetching } = useFetchResourcePlansByResource({ basePath, customerId, resourceId });
  const dayTextColor = getDayText();
  const changeView = (event) => {
    let calView;

    switch (event.target.value) {
      case "week":
        calView = { timeline: { type: "week", eventList: true } };
        break;
      case "month":
      default:
        calView = { timeline: { type: "month", eventList: true } };
        break;
    }

    setView(event.target.value);
    setCalView(calView);
  };

  const invalid = [
    {
      recurring: {
        repeat: "daily",
        until: dayjs().subtract(1, "day").toDate(),
      },
    },
  ];

  const renderResource = (resource) => {
    return <div className="text-md p-2">{resource.name}</div>;
  };

  const renderHeader = () => {
    return (
      <>
        <CalendarNav className="md-event-listing-nav" />
        <div className="md-event-listing-picker">
          <SegmentedGroup value={view} onChange={changeView}>
            <SegmentedItem value="month">月</SegmentedItem>
            <SegmentedItem value="week">週</SegmentedItem>
          </SegmentedGroup>
        </div>
        <CalendarPrev className="md-event-listing-prev" />
        <CalendarToday className="md-event-listing-today" />
        <CalendarNext className="md-event-listing-next" />
      </>
    );
  };

  const renderScheduleEvent = useCallback((data) => {
    const original = data.original;
    const color = data.color || "#14126f";

    return (
      <div className="p-2" style={{ borderColor: color, background: color }}>
        <div className="font-weight-bold text-truncate text-sm text-white">{original.site.name}</div>
        <div className="text-xs text-white">
          {dayjs(original.start).isSame(dayjs(original.end), "day")
            ? dayjs(original.start).format("M/D")
            : `${dayjs(original.start).format("M/D")}-${dayjs(original.end).format("M/D")}`}
        </div>
      </div>
    );
  }, []);

  const renderDay = (args) => {
    const date = args.date;
    return (
      <div className="p-2 text-center">
        <div className="text-lg">{dayjs(date).date()}</div>
        <div className={`text-md text-${dayTextColor(date)}`}>{dayjs(date).format("dd")}</div>
      </div>
    );
  };

  const onPageChange = (event) => {
    setFrom(dayjs(event.firstDay).unix());
    setTo(dayjs(event.lastDay).unix());
  };

  const colors: MbscCalendarColor[] =
    runningTimes?.map((runningTime) => {
      const date = dayjs(runningTime.date);

      return {
        resource: "results",
        start: date.hour(8).startOf("hour").toDate(),
        end: date.hour(17).startOf("hour").toDate(),
        cssClass: `bg-${runningTime.frequencyOfUse}`,
      };
    }) || [];

  if (!plans) return <></>;

  const data = plans.map((plan) => ({
    ...plan,
    start: dayjs.unix(plan.startedAt).toDate(),
    end: dayjs.unix(plan.endedAt).toDate(),
    resourceId: plan.resource.id,
    resource: "plans",
    color: plan.resource.color,
  }));

  return (
    <>
      <LoadingOverlay active={isLoading || isFetching} spinner text="読込中です">
        <Eventcalendar
          view={calView}
          data={data}
          resources={[
            { name: "計画", id: "plans" },
            { name: "実績", id: "results" },
          ]}
          colors={colors}
          invalid={invalid}
          renderResource={renderResource}
          renderHeader={renderHeader}
          renderScheduleEvent={renderScheduleEvent}
          renderDay={renderDay}
          onPageChange={onPageChange}
        />
      </LoadingOverlay>
    </>
  );
};

export default Timeline;
